import { useRef } from 'react'

import { useScreen } from '../../../hooks'
import { PageLoader } from '../../Page'
import { useWidgetListPipeline, WidgetListPipeline } from '../../Widget'
import {
  useDocFlowContext,
  useDocFlowEpostSettings,
  useDocFlowHandleFinish,
  useDocFlowSendingFileProcess,
  useDocFlowSendingWidgets,
  useSendingPhase,
} from '../DocFlow.hooks'
import { NextPhaseButton } from '../DocFlowProfileHeaderActions'
import { DocFlowSendingProvider } from '../DocFlowSendingProvider'

import { DocFlowSendingWidgetType } from './DocFlowSending.constants'
import {
  getDocFlowSendingWidgets,
  isDocFlowSendingWidgetTypeResult,
  renderWidget,
} from './DocFlowSending.utils'

import * as styles from './DocFlowSending.module.css'

function DocFlowSending() {
  const { docFlow } = useDocFlowContext()

  const { isMobileScreenSize } = useScreen()

  const emailFormikRef = useRef(null)
  const emailAIDraftRef = useRef(false)
  const epostFormikRef = useRef(null)

  const widgets = getDocFlowSendingWidgets()

  const {
    activeWidget,
    handleClickNext,
    handleClickPrevious,
    setActiveWidget,
  } = useWidgetListPipeline({
    initialActive: DocFlowSendingWidgetType.CommunicationSettings,
    widgets,
  })

  const {
    getFileProcess,
    handleUpdateFileProcess,
    isProcessDataInitiated,
    isProcessDataLoading,
    processData,
    setProcessDataLoading,
  } = useDocFlowSendingFileProcess({ setActiveWidget })

  const {
    emailBlackList,
    isPageLoader,
    sendingWidgetContent,
    setEmailBlackList,
    setPageLoader,
    tableData,
  } = useSendingPhase({
    activeWidget,
    getFileProcess,
  })

  const {
    calculatingData,
    epostNotificationsData,
    epostSettings,
    epostSupportModalData,
    getEpostSettings,
    handleUpdateEpostSettings,
    isCalculated,
    isCalculating,
    isEpostSettingsLoading,
    noEpostSettings,
  } = useDocFlowEpostSettings({
    activeWidget,
    epostFormikRef,
    processData,
  })

  const { handleNextWidget, handlePreviousWidget } = useDocFlowSendingWidgets({
    activeWidget,
    onClickNext: handleClickNext,
    onClickPrevious: handleClickPrevious,
    onUpdateFileProcess: handleUpdateFileProcess,
  })

  const { handleNextPhase } = useDocFlowHandleFinish({
    onReloadProcess: getFileProcess,
  })

  const isResultWidget = isDocFlowSendingWidgetTypeResult(activeWidget)

  return (
    <div className={styles.DocFlowSending}>
      {isPageLoader ? <PageLoader /> : null}

      <DocFlowSendingProvider
        activeWidget={activeWidget}
        calculatingData={calculatingData}
        emailAIDraftRef={emailAIDraftRef}
        emailBlackList={emailBlackList}
        emailFormikRef={emailFormikRef}
        epostFormikRef={epostFormikRef}
        epostNotificationsData={epostNotificationsData}
        epostSettings={epostSettings}
        epostSupportModalData={epostSupportModalData}
        getEpostSettings={getEpostSettings}
        getFileProcess={getFileProcess}
        handleClickNextWidget={handleNextWidget}
        handleClickPreviousWidget={handlePreviousWidget}
        handleUpdateEpostSettings={handleUpdateEpostSettings}
        handleUpdateFileProcess={handleUpdateFileProcess}
        isCalculated={isCalculated}
        isCalculating={isCalculating}
        isEpostSettingsLoading={isEpostSettingsLoading}
        isPageLoader={isPageLoader}
        isProcessDataLoading={isProcessDataLoading}
        noEpostSettings={noEpostSettings}
        processData={processData}
        setActiveWidget={setActiveWidget}
        setEmailBlackList={setEmailBlackList}
        setPageLoader={setPageLoader}
        setProcessDataLoading={setProcessDataLoading}
        usersTableData={tableData}
        widgets={widgets}
      >
        {!isMobileScreenSize ? sendingWidgetContent : null}

        <WidgetListPipeline
          activeWidget={activeWidget}
          activeWidgetMobileContent={sendingWidgetContent}
          isSkeleton={!isProcessDataInitiated}
          items={widgets}
          renderItem={renderWidget}
          onClickPrevious={handlePreviousWidget}
          onSubmit={handleNextWidget}
        >
          {docFlow?.permissions.can_move_to_next_phase && isResultWidget ? (
            <NextPhaseButton
              disabled={isProcessDataLoading}
              text='DocumentStapling.SendingFinishBtnKey'
              onNextPhase={handleNextPhase}
            />
          ) : null}
        </WidgetListPipeline>
      </DocFlowSendingProvider>
    </div>
  )
}

export default DocFlowSending
